/**
 * Moderation Work Queue State Components
 *
 * Various state displays for the moderation work queue page
 */

import { srOnly } from '@core/styles/utilities.css';
import { Button, ButtonType, Heading, Text, Icon, IconSize, Completed, Warning } from '@soteria/design-system-ve';
import type { ReactElement } from 'react';

import * as styles from '../ModerationWorkQueuePage.css';

export function EmptyQueueState({ onReturn, hasActiveFilter }: { onReturn: () => void; hasActiveFilter?: boolean }): ReactElement {
  return (
    <div className={styles.container}>
      <Heading className={srOnly} level="h1">
        Moderation Queue
      </Heading>
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>
          <Icon icon={Completed} iconSize={IconSize.XLarge} />
        </div>
        <Heading level="h2">{hasActiveFilter ? 'No Matching Cases' : 'No Cases Available'}</Heading>
        <Text>
          {hasActiveFilter
            ? 'No cases match the selected queue filter. Try selecting a different filter or "All Cases".'
            : 'There are no moderation cases available for this topic at the moment.'}
        </Text>
        <Button buttonType={ButtonType.Primary} onClick={onReturn}>
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
}

export function CompletedState({ totalCases, onReturn }: { totalCases: number; onReturn: () => void }): ReactElement {
  return (
    <div className={styles.container}>
      <Heading className={srOnly} level="h1">
        Moderation Queue
      </Heading>
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>
          <Icon icon={Completed} iconSize={IconSize.XLarge} />
        </div>
        <Heading level="h2">All Cases Completed!</Heading>
        <Text>
          You've reviewed all {totalCases} case{totalCases !== 1 ? 's' : ''} in this batch.
        </Text>
        <Button buttonType={ButtonType.Primary} onClick={onReturn}>
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
}

export function LoadingState(): ReactElement {
  return (
    <div className={styles.container}>
      <Heading className={srOnly} level="h1">
        Moderation Queue
      </Heading>
      <div className={styles.emptyState}>
        <Heading level="h2">Loading Cases...</Heading>
        <Text>Please wait while we fetch your assigned cases.</Text>
      </div>
    </div>
  );
}

export function ErrorState({ message, onReturn }: { message: string; onReturn: () => void }): ReactElement {
  return (
    <div className={styles.container}>
      <Heading className={srOnly} level="h1">
        Moderation Queue
      </Heading>
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>
          <Icon icon={Warning} iconSize={IconSize.XLarge} />
        </div>
        <Heading level="h2">Error Loading Cases</Heading>
        <Text>{message}</Text>
        <Button buttonType={ButtonType.Primary} onClick={onReturn}>
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
}
